import { supabaseClient } from '../config/supabase';
import { hashPassword } from '../utils/passwordHash';

// Characters chosen to avoid visual mix-ups when someone hand-types a temp
// password off a screen (no 0/O, no 1/I/l).
const TEMP_PASSWORD_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

export function generateTempPassword(length = 10) {
  let out = '';
  for (let i = 0; i < length; i++) {
    out += TEMP_PASSWORD_CHARS[Math.floor(Math.random() * TEMP_PASSWORD_CHARS.length)];
  }
  return out;
}

export const userService = {
  // All users (for the Senior Manager's team management list).
  async getAllUsers() {
    const { data, error } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status, reports_to_id, password_reset_required, created_at')
      .order('role', { ascending: true })
      .order('full_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Planners assigned to a specific manager (reports_to_id = managerId).
  // Active only - a deactivated planner shouldn't be pickable as a new
  // coaching session's recipient.
  async getTeamRoster(managerId) {
    const { data, error } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status')
      .eq('role', 'planner')
      .eq('status', 'active')
      .eq('reports_to_id', managerId);

    if (error) throw error;
    return data || [];
  },

  async getAllPlanners() {
    const { data, error } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status, reports_to_id')
      .eq('role', 'planner')
      .order('full_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getAllManagers() {
    const { data, error } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status')
      .eq('role', 'manager')
      .order('full_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Managers who report to a specific Senior Manager.
  async getManagersForSeniorManager(seniorManagerId) {
    const { data, error } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status')
      .eq('role', 'manager')
      .eq('reports_to_id', seniorManagerId)
      .order('full_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Planners two hops down from a Senior Manager: every planner whose
  // manager reports to this Senior Manager. This is the scope for
  // "coach a planner directly" and for the Senior Manager's dashboard -
  // a Senior Manager should only ever see their own branch's people.
  async getPlannersForSeniorManager(seniorManagerId) {
    const managers = await this.getManagersForSeniorManager(seniorManagerId);
    const managerIds = managers.map((m) => m.id);

    // A planner can also report straight to the Senior Manager, with no
    // Manager in between (e.g. the "Minokawa Direct" group) - always
    // include the Senior Manager's own id alongside their Managers' so
    // those planners show up here too, not just the two-hops-down ones.
    // Never skip the query just because managerIds is empty - a brand
    // new branch with no Managers yet could still have direct reports.
    const { data, error } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status, reports_to_id')
      .eq('role', 'planner')
      .eq('status', 'active')
      .in('reports_to_id', [...managerIds, seniorManagerId]);

    if (error) throw error;
    return data || [];
  },

  // Everyone in a Senior Manager's own branch for "Manage Team" - the
  // managers reporting to them, and the planners reporting to those
  // managers. Deliberately never includes other Senior Managers or Admin:
  // a Senior Manager only ever manages their own branch.
  async getBranchTeam(seniorManagerId) {
    const { data: managersRaw, error: managersError } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status, reports_to_id, password_reset_required, created_at')
      .eq('role', 'manager')
      .eq('reports_to_id', seniorManagerId);

    if (managersError) throw managersError;
    const managers = managersRaw || [];
    const managerIds = managers.map((m) => m.id);

    // A planner can also report straight to the Senior Manager, with no
    // Manager in between (e.g. the "Minokawa Direct" group) - always
    // include the Senior Manager's own id so those planners show up on
    // Manage Team too, not just the two-hops-down ones.
    const { data: plannersRaw, error: plannersError } = await supabaseClient
      .from('coaching_users')
      .select('id, username, full_name, role, branch, status, reports_to_id, password_reset_required, created_at')
      .eq('role', 'planner')
      .in('reports_to_id', [...managerIds, seniorManagerId]);

    if (plannersError) throw plannersError;
    const planners = plannersRaw || [];

    return [...managers, ...planners].sort((a, b) => {
      if (a.role !== b.role) return a.role === 'manager' ? -1 : 1;
      return a.full_name.localeCompare(b.full_name);
    });
  },

  // Creates a new user with a random temporary password. Returns the
  // created row plus the plaintext temp password (shown once so the
  // Senior Manager can relay it - it is not recoverable after this call).
  async createUser({ fullName, username, role, branch, reportsToId }) {
    const tempPassword = generateTempPassword();
    const passwordHash = await hashPassword(tempPassword);

    const { data, error } = await supabaseClient
      .from('coaching_users')
      .insert({
        username,
        full_name: fullName,
        role,
        // coaching_users.branch is NOT NULL - an empty string (not null)
        // for "no branch set" (e.g. Admin accounts).
        branch: branch || '',
        reports_to_id: reportsToId || null,
        status: 'active',
        password: passwordHash,
        password_reset_required: true,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error(`Username "${username}" is already taken`);
      }
      throw error;
    }

    return { user: data, tempPassword };
  },

  // Generates and sets a new temporary password for an existing user.
  async resetPassword(userId) {
    const tempPassword = generateTempPassword();
    const passwordHash = await hashPassword(tempPassword);

    const { error } = await supabaseClient
      .from('coaching_users')
      .update({ password: passwordHash, password_reset_required: true })
      .eq('id', userId);

    if (error) throw error;
    return tempPassword;
  },

  // A logged-in user setting their own new password (clears the
  // must-change flag so they aren't prompted again).
  async changeOwnPassword(userId, newPassword) {
    const passwordHash = await hashPassword(newPassword);

    const { error } = await supabaseClient
      .from('coaching_users')
      .update({ password: passwordHash, password_reset_required: false })
      .eq('id', userId);

    if (error) throw error;
    return passwordHash;
  },

  async setStatus(userId, status) {
    const { error } = await supabaseClient
      .from('coaching_users')
      .update({ status })
      .eq('id', userId);

    if (error) throw error;
  },

  async assignManager(userId, reportsToId) {
    const { error } = await supabaseClient
      .from('coaching_users')
      .update({ reports_to_id: reportsToId || null })
      .eq('id', userId);

    if (error) throw error;
  },

  // Lets a planner's own Manager label them for easier coaching planning -
  // one of the 4 fixed categories shown on My Planners (Core Leaders /
  // Full-Time Planners / Part-Time Planners L1 / Part-Time Planners L2),
  // or cleared back to unlabeled. Purely descriptive - doesn't affect
  // roster membership, stats, or anything else the rest of the app
  // computes from coaching_users.
  async updatePlannerCategory(userId, category) {
    const { error } = await supabaseClient
      .from('coaching_users')
      .update({ planner_category: category || null })
      .eq('id', userId);

    if (error) throw error;
  },

  // Changes an existing person's role - the "promote a planner to manager"
  // (or move a manager back to planner) case that comes up on real
  // promotions, without having to delete and recreate the account and lose
  // their coaching history. 'planner' and 'manager' are grantable by a
  // Senior Manager; 'senior_manager' is only ever offered in the UI to an
  // Admin (Admin sits above Senior Manager, so only Admin hands that role
  // out or takes it back).
  //
  // Promoting someone to manager sets their reports_to_id to the acting
  // Senior Manager/Admin (the same rule new managers get on creation - a
  // Senior Manager only ever manages their own branch, so there's no one
  // else to pick). Any other role change clears reports_to_id - a planner
  // reassigns to a new manager like normal, and a Senior Manager has no
  // manager-side reporting line at all.
  async updateRole(userId, newRole, actingManagerOrSeniorManagerId) {
    if (!['planner', 'manager', 'senior_manager'].includes(newRole)) {
      throw new Error('Role can only be changed to planner, manager, or senior manager here');
    }
    const updates = { role: newRole };
    updates.reports_to_id = newRole === 'manager' ? actingManagerOrSeniorManagerId : null;

    const { error } = await supabaseClient
      .from('coaching_users')
      .update(updates)
      .eq('id', userId);

    if (error) throw error;
  },

  // Every coaching_records row this person appears in, either side -
  // what a "delete and download a backup first" flow needs to export
  // before the rows are gone for good.
  async getCoachingRecordsForUser(userId) {
    const { data, error } = await supabaseClient
      .from('coaching_records')
      .select('*')
      .or(`planner_id.eq.${userId},coach_id.eq.${userId}`);

    if (error) throw error;
    return data || [];
  },

  // Hard delete only makes sense for a user with no coaching history -
  // deleting someone with coaching_records would either fail or destroy
  // real organizational history. Callers should offer "deactivate"
  // instead when this throws.
  //
  // { force: true } skips that guard and wipes the person's coaching
  // history along with their account instead of blocking - meant for
  // "delete this planner anyway", where the caller has already downloaded
  // a backup of their records (see getCoachingRecordsForUser) before
  // calling this.
  async deleteUser(userId, { force = false } = {}) {
    const { count: coachCount } = await supabaseClient
      .from('coaching_records')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', userId);
    const { count: plannerCount } = await supabaseClient
      .from('coaching_records')
      .select('id', { count: 'exact', head: true })
      .eq('planner_id', userId);

    const hasHistory = (coachCount || 0) > 0 || (plannerCount || 0) > 0;

    if (hasHistory && !force) {
      throw new Error(
        'This person has coaching history attached to their account. Deactivate them instead of deleting, so that history is preserved.'
      );
    }

    if (hasHistory && force) {
      // Clear their coaching history first so the account delete below
      // doesn't fail on it - the caller is expected to have already saved
      // a copy of these rows.
      await supabaseClient.from('coaching_records').delete().eq('planner_id', userId);
      await supabaseClient.from('coaching_records').delete().eq('coach_id', userId);
    }

    const { error } = await supabaseClient.from('coaching_users').delete().eq('id', userId);
    if (error) throw error;
  },
};
